import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  Twitter,
} from "lucide-react";

import { footerLinks, siteConfig } from "../../../data/siteConfig";
import { telegramLink, whatsappLink } from "../../../utils/contact";

const footerLinkClass =
  "inline-flex rounded-md text-sm font-medium text-slate-700 transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-slate-300 dark:hover:text-primary-300";

const socialLinkClass =
  "flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition-all hover:-translate-y-0.5 hover:border-primary-500 hover:bg-primary-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-primary-500 dark:hover:bg-primary-500 dark:hover:text-white";

const FooterComponent: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-slate-200 bg-slate-100 text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-white">
      <div className="w-full px-4 mx-auto max-w-7xl py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-x-10">
          <div className="col-span-2">
            <Link
              to="/"
              aria-label={`Accueil — ${siteConfig.name}`}
              className="inline-flex items-center mb-4 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <img
                src="/images/logo/fulllogo.png"
                alt={siteConfig.name}
                loading="lazy"
                decoding="async"
                className="h-16 w-auto max-w-[220px] object-contain"
              />
            </Link>

            <p className="max-w-sm mb-6 text-sm font-medium leading-7 text-slate-700 dark:text-slate-300">
              {siteConfig.shortDescription}
            </p>

            <div className="flex flex-wrap items-center gap-2" aria-label="Réseaux sociaux">
              <a
                href={whatsappLink(
                  siteConfig.whatsappNumber,
                  "Bonjour, j’ai une question concernant OneDealPlace."
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 transition-all hover:-translate-y-0.5 hover:border-emerald-600 hover:bg-emerald-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300 dark:hover:border-emerald-500 dark:hover:bg-emerald-600 dark:hover:text-white"
                aria-label="Nous contacter sur WhatsApp"
              >
                <MessageCircle size={17} />
              </a>

              <a
                href={telegramLink(siteConfig.telegramHandle)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-700 transition-all hover:-translate-y-0.5 hover:border-sky-600 hover:bg-sky-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-300 dark:hover:border-sky-500 dark:hover:bg-sky-600 dark:hover:text-white"
                aria-label="Nous contacter sur Telegram"
              >
                <Send size={17} />
              </a>

              <a href={siteConfig.socials.facebook} target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="Facebook">
                <Facebook size={17} />
              </a>
              <a href={siteConfig.socials.instagram} target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="Instagram">
                <Instagram size={17} />
              </a>
              <a href={siteConfig.socials.x} target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="X">
                <Twitter size={17} />
              </a>
              <a href={siteConfig.socials.linkedin} target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="LinkedIn">
                <Linkedin size={17} />
              </a>
            </div>
          </div>

          <div>
            <h2 className="mb-4 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
              Plateforme
            </h2>
            <ul className="space-y-3">
              {footerLinks.plateforme.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="mb-4 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
              Entreprise
            </h2>
            <ul className="space-y-3">
              {footerLinks.entreprise.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={footerLinkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 sm:col-span-1">
            <h2 className="mb-4 text-xs font-extrabold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
              Contact
            </h2>

            <ul className="space-y-4 text-sm font-medium text-slate-700 dark:text-slate-300">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300">
                  <MapPin size={15} />
                </span>
                <span className="pt-1.5 leading-5">{siteConfig.address}</span>
              </li>

              <li>
                <a
                  href={`tel:${siteConfig.supportPhone.replace(/\s/g, "")}`}
                  className="flex items-center gap-3 transition-colors rounded-lg hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300"
                >
                  <span className="grid w-8 h-8 rounded-lg shrink-0 place-items-center bg-primary-100 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300">
                    <Phone size={15} />
                  </span>
                  <span>{siteConfig.supportPhone}</span>
                </a>
              </li>

              <li>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center min-w-0 gap-3 transition-colors rounded-lg hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300"
                >
                  <span className="grid w-8 h-8 rounded-lg shrink-0 place-items-center bg-primary-100 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300">
                    <Mail size={15} />
                  </span>
                  <span className="break-all">{siteConfig.email}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-4 pt-6 mt-12 border-t border-slate-300 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
            &copy; {currentYear} {siteConfig.legalEntity}. Tous droits réservés.
          </p>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-xs font-medium transition-colors rounded text-slate-600 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-slate-400 dark:hover:text-primary-300"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterComponent;
